/**
 * LanguagesList component.
 */
import Animatable from '../../../atoms/container/animatable';
import ArrowRightIcon from '../../../atoms/icons/arrowRight';
import Colors from '@shared/assets/style/default/color';
import Direction from '../../../../../shared/types/enums/direction';
import Focus from '../../../../../shared/types/enums/focus';
import LanguageItem from '../../../elements/language/languageItem';
import LanguageListArrowContainer from '../../../atoms/container/languageListArrowContainer';
import LanguageListContainer from '../../../atoms/container/languageListContainer';
import List from '../list';
import TranslationManager from '../../../../../shared/assets/locale/translationsManager';
import useTransform from '../../../../../shared/components/utils/hooks/useTransform';
import { LanguageListProps as Props } from '../../../../../shared/types/props/language';
import { magicRemoteSelector } from '../../../../../shared/store/slices/magicRemote.slice';
import { useEffect, useRef } from 'react';
import {
  focusSelector,
  pushFocus,
} from '../../../../../shared/store/slices/focus.slice';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../shared/store/hooks';

/**
 * Constants
 * - LANGUAGE_DISPLACEMENT: The displacement of the language list.
 */
const LANGUAGE_DISPLACEMENT = 62;

/**
 * Displays a list of languages to choose from.
 */
export default function LanguagesList({ animate = true, languages }: Props) {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  const languageIndex = languages.findIndex((item) => {
    return TranslationManager.currentLanguage === item;
  });

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const { focus } = useAppSelector(focusSelector);
  const { enabled } = useAppSelector(magicRemoteSelector);

  // Refs
  const focusRef = useRef(focus);
  focusRef.current = focus;

  // Transform

  const [style, setStyle] = useTransform(
    languageIndex,
    LANGUAGE_DISPLACEMENT,
    Direction.VERTICAL
  );

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onChange = (index: number, increment: number | undefined) => {
    if (increment) {
      setStyle(increment);
      TranslationManager.setLanguage(languages[index]);
    }
  };

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    focusRef.current !== Focus.LANGUAGES &&
      dispatch(pushFocus(Focus.LANGUAGES));
  }, []);

  // ------------------------------------------------------------
  // Return values
  // ------------------------------------------------------------

  return (
    <LanguageListContainer animate={animate} id={'languageListContainer'}>
      <Animatable
        style={animate ? style : undefined}
        animation={{ transition: 'transform 0.15s' }}
        id={'languageItem'}
      >
        <List
          direction={Direction.VERTICAL}
          focus={Focus.LANGUAGES}
          initialIndex={languageIndex}
          onChange={onChange}
        >
          {languages.map((item, index) => {
            return <LanguageItem index={index} key={index} item={item} />;
          })}
        </List>
      </Animatable>
      {!enabled && (
        <LanguageListArrowContainer
          offset={animate ? 0 : LANGUAGE_DISPLACEMENT * languageIndex}
          id={'arrowContainer'}
        >
          <ArrowRightIcon color={Colors.selector} width={18} />
        </LanguageListArrowContainer>
      )}
    </LanguageListContainer>
  );
}
